import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'src/components/ui/badge';
import { ProductImage } from 'src/features/novelties/components/ProductImage';
import { SalesPotentialEditor } from 'src/features/novelties/components/SalesPotentialEditor';
import { cn } from 'src/lib/utils';
import { NoveltyProductData } from 'src/services/api/getNoveltiesBestCandidates';

function formatPrice(
  price: number | null,
  currency: string | null,
  locale: string,
): string | null {
  if (price === null || !currency || currency === 'NON DEFINI') {
    return null;
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
  }).format(price);
}

type ProductCardHeaderProps = React.HTMLAttributes<HTMLDivElement> & {
  product: NoveltyProductData;
  isExpanded: boolean;
  className?: string;
};

export const ProductCardHeader = forwardRef<
  HTMLDivElement,
  ProductCardHeaderProps
>(({ product, isExpanded, className, ...props }, ref) => {
  const { t, i18n } = useTranslation('novelties');

  const formattedPurchasePrice = formatPrice(
    product.purchase_price,
    product.purchase_currency,
    i18n.language,
  );

  return (
    <div
      ref={ref}
      className={cn(
        'hover:bg-layout-hover flex w-full cursor-pointer items-center justify-between p-4 text-left transition-colors',
        isExpanded && 'bg-layout-hover border-b border-border-default',
        className,
      )}
      {...props}
    >
      <div className='flex items-center gap-4'>
        <div className='bg-primary-default/10 flex size-10 items-center justify-center rounded-lg'>
          {isExpanded ? (
            <ChevronDown className='size-5 text-primary-default' />
          ) : (
            <ChevronRight className='size-5 text-primary-default' />
          )}
        </div>
        <ProductImage
          src={product.image_url}
          alt={product.title || product.sku}
          size='md'
        />
        <div className='flex flex-col gap-1'>
          <div className='flex items-center gap-2'>
            <span className='font-mono text-sm font-semibold'>
              {product.sku}
            </span>
            {product.similarities.length > 0 && (
              <Badge variant='secondary' className='text-xs'>
                {product.similarities.length}{' '}
                {product.similarities.length === 1
                  ? t('candidate')
                  : t('candidates')}
              </Badge>
            )}
          </div>
          <div className='flex items-center gap-2'>
            {product.title && (
              <span className='max-w-md truncate text-sm text-layout-foreground-secondary'>
                {product.title}
              </span>
            )}
            {formattedPurchasePrice && (
              <Badge
                variant='outline'
                className='flex items-center gap-1 text-xs'
              >
                <span className='font-normal text-layout-foreground-secondary'>
                  {t('Purchase Price')}:
                </span>
                <span className='font-mono font-medium'>
                  {formattedPurchasePrice}
                </span>
              </Badge>
            )}
          </div>
        </div>
      </div>
      <div className='flex items-center gap-4'>
        <SalesPotentialEditor
          sku={product.sku}
          salesPotential={product.sales_potential}
        />
      </div>
    </div>
  );
});

ProductCardHeader.displayName = 'ProductCardHeader';
